/**
 * React Query hooks for notifications data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Notification, NotificationFilter } from '@/types/notifications';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const QUERY_KEY = 'notifications';

/**
 * Fetch notifications with optional filters
 */
export function useNotifications(filter?: NotificationFilter) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEY, 'list', filter],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      let query = supabase
        .from('notifications' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filter?.status === 'unread') {
        query = query.eq('is_read', false);
      } else if (filter?.status === 'read') {
        query = query.eq('is_read', true);
      }

      if (filter?.type && filter.type !== 'all') {
        query = query.eq('type', filter.type);
      }

      if (filter?.category && filter.category !== 'all') {
        query = query.eq('category', filter.category);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as unknown as Notification[];
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 3,
  });
}

/**
 * Fetch unread notification count for badge
 */
export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEY, 'unread-count'],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { data, error } = await supabase.rpc('get_unread_notification_count' as any);

      if (error) throw error;
      return (data || 0) as number;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 1000 * 60, // Refetch every minute
  });
}

/**
 * Mark single notification as read/unread
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, isRead }: { id: string; isRead: boolean }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('notifications' as any)
        .update({
          is_read: isRead,
          read_at: isRead ? new Date().toISOString() : null,
        } as any)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as Notification;
    },
    onMutate: async ({ id, isRead }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });

      // Snapshot previous value
      const previousNotifications = queryClient.getQueryData([QUERY_KEY, 'list']);
      const previousCount = queryClient.getQueryData([QUERY_KEY, 'unread-count']);

      // Optimistically update notifications list
      queryClient.setQueriesData<Notification[]>(
        { queryKey: [QUERY_KEY, 'list'] },
        (old) => {
          if (!old) return old;
          return old.map((notif) =>
            notif.id === id
              ? { ...notif, is_read: isRead, read_at: isRead ? new Date().toISOString() : null }
              : notif
          );
        }
      );

      // Optimistically update count
      queryClient.setQueryData<number>([QUERY_KEY, 'unread-count'], (old = 0) => {
        return isRead ? Math.max(0, old - 1) : old + 1;
      });

      return { previousNotifications, previousCount };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData([QUERY_KEY, 'list'], context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData([QUERY_KEY, 'unread-count'], context.previousCount);
      }
      toast.error('Failed to update notification');
    },
    onSuccess: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { error } = await supabase.rpc('mark_all_notifications_read' as any);

      if (error) throw error;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });

      const previousNotifications = queryClient.getQueryData([QUERY_KEY, 'list']);
      const previousCount = queryClient.getQueryData([QUERY_KEY, 'unread-count']);

      // Optimistically update all notifications to read
      queryClient.setQueriesData<Notification[]>(
        { queryKey: [QUERY_KEY, 'list'] },
        (old) => {
          if (!old) return old;
          return old.map((notif) => ({
            ...notif,
            is_read: true,
            read_at: notif.is_read ? notif.read_at : new Date().toISOString(),
          }));
        }
      );

      // Set count to zero
      queryClient.setQueryData([QUERY_KEY, 'unread-count'], 0);

      return { previousNotifications, previousCount };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData([QUERY_KEY, 'list'], context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData([QUERY_KEY, 'unread-count'], context.previousCount);
      }
      toast.error('Failed to mark all as read');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('All notifications marked as read');
    },
  });
}

/**
 * Real-time subscription to notification updates
 */
export function useNotificationSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Invalidate queries on any change
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);
}
